import { subject as tag } from '@casl/ability'
import { useAbility } from '@casl/react'
import { ActionIcon, Alert, Button, Center, Grid, Group, Loader, Menu, Tabs, Text, Tooltip } from '@mantine/core'
import { type FormErrors, schemaResolver, useForm } from '@mantine/form'
import { useDocumentTitle, useHotkeys, useMediaQuery } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconBan, IconCopy, IconDotsVertical, IconFileOff, IconLock, IconPencil, IconTrash } from '@tabler/icons-react'
import {
  type ApiIssue,
  type AppAbility,
  copyOf,
  type DocStatus,
  docStatusOf,
  type FormFieldMeta,
  fieldEditable,
  fieldsEditableAfterSubmit,
  labels,
  lifecycleAllows,
  type RefMap,
  type ResolvedField,
  type Resource,
} from '@tnn/core'
import { currentLocale } from '@tnn/i18n'
import { EmptyState, Page, SectionCard, scrollToTop } from '@tnn/ui'
import { type KeyboardEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker, useParams, useSearchParams } from 'react-router'
import { isApiError } from '../api/client'
import { type Rec, useActivity, useDeleteRecord, useLifecycle, useResourceRecord, useSaveRecord } from '../api/resources'
import { useNavigate } from '../app/scope'
import { useGoBack, useStackEntry } from '../app/stack'
import { useSession } from '../auth/session'
import { GuideButton } from '../guides/guides'
import { TaskTargetProvider } from '../tasks/handoff'
import { pagePath, type ResourcePage } from './config'
import { AfterSubmitBadge, DocActivity } from './DocActivity'
import { CancelDialog, confirmAsync, DocBanner, DocStatusPill } from './DocLifecycle'
import { FieldInput } from './FieldInput'
import { fieldText } from './FieldValue'
import classes from './ResourceForm.module.css'
import { justSubmitted } from './submitted'
import { TakenWarning } from './TakenWarning'

/**
 * Create (`/<path>/new`) and edit (`/<path>/:id`) page for any resource. `/<path>/new?copy=<id>`: a new record started
 * from that one (ทำสำเนา, `resource.duplicate`).
 */
export function ResourceFormPage({ page }: { page: ResourcePage }) {
  const { resource } = page
  const { id } = useParams()
  const [params] = useSearchParams()
  const { t } = useTranslation()
  const isNew = !id || id === 'new'
  const copyId = (isNew && resource.duplicate && params.get('copy')) || undefined
  // A new record reads the one it copies; nothing else
  const query = useResourceRecord(resource, isNew ? copyId : id)

  if ((!isNew || copyId) && query.isPending) {
    return (
      <Center flex={1}>
        <Loader type="dots" />
      </Center>
    )
  }
  if ((!isNew || copyId) && (query.error || !query.data)) {
    return <EmptyState icon={IconFileOff} title={t(isApiError(query.error, 'FORBIDDEN') ? 'errors.forbidden' : 'errors.notFound')} />
  }
  const item = query.data?.item
  return (
    <RecordForm
      key={copyId ? `copy-${copyId}` : (id ?? 'new')}
      page={page}
      record={isNew ? undefined : item}
      source={copyId ? item : undefined}
      refs={query.data?.refs ?? {}}
    />
  )
}

function emptyValue(f: ResolvedField): unknown {
  if (f.defaultValue !== undefined) return f.defaultValue
  if (f.type === 'boolean') return false
  if (f.type === 'multiselect' || f.type === 'tags' || f.type === 'json') return []
  if (
    f.type === 'number' ||
    f.type === 'integer' ||
    f.type === 'money' ||
    f.type === 'percent' ||
    f.type === 'text' ||
    f.type === 'textarea'
  )
    return ''
  return null
}

type EditableField = ResolvedField & { form: FormFieldMeta }

function editable(resource: Resource): EditableField[] {
  return resource.fieldList.filter((f): f is EditableField => !f.readOnly && f.form !== false)
}

/** Form values → API body: empty inputs become null (clear) or are left out. */
function toBody(resource: Resource, values: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const f of editable(resource)) {
    const v = values[f.name]
    if (v === '' || v === undefined || v === null) {
      if (f.nullable) body[f.name] = null
      continue
    }
    body[f.name] = v
  }
  return body
}

/** Only what the user changed, so a save never undoes changes made elsewhere meanwhile (assign, status) */
function changed(body: Record<string, unknown>, before: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(body).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(before[k] ?? null)))
}

function issuesToErrors(issues: ApiIssue[] = []): FormErrors {
  return Object.fromEntries(issues.map((i) => [i.path.join('.'), i.message]))
}

/** `source`: the record a new one copies (`?copy=`), its `refs` those of the source */
function RecordForm({ page, record, source, refs }: { page: ResourcePage; record?: Rec; source?: Rec; refs: RefMap }) {
  const { resource, form: opts = {} } = page
  const path = pagePath(resource)
  const { t, i18n } = useTranslation()
  const locale = currentLocale(i18n)
  const navigate = useNavigate()
  const ability = useAbility<AppAbility>()
  const phone = useMediaQuery('(max-width: 47.99em)')
  const formRef = useRef<HTMLFormElement>(null)
  const formId = useId()
  const saveAndNew = useRef(false)
  // Set by "Submit" / "Save and submit": the form saves (when needed), then submits the document
  const thenSubmit = useRef(false)
  const [busy, setBusy] = useState<'save' | 'submit' | 'amend'>()
  const [cancelling, setCancelling] = useState(false)
  // A submitted document opens read-only; "edit after submit" unlocks its allow-on-submit fields
  const [editingAfter, setEditingAfter] = useState(false)
  const isNew = !record
  const { me, appId } = useSession()
  // A link can start a new record from somewhere else: `/payments/new?invoices=…` (see `defaults`)
  const [params] = useSearchParams()
  const defaultsOf = () => opts.defaults?.({ me, params }) ?? {}
  const tabs = opts.tabs ?? [{ key: 'main', label: '', sections: resource.sections as (string | string[])[] }]
  const [tab, setTab] = useState(tabs[0]?.key ?? 'main')
  // A new record on a phone, a step at a time, the last one to look it over — when the page asks for it
  const stepped = phone && isNew ? opts.phoneSteps : undefined
  const [stepAt, setStepAt] = useState(0)
  // Sent from the review to change one part: done with it, straight back to the review
  const [fromReview, setFromReview] = useState(false)
  const reviewing = !!stepped && stepAt >= stepped.steps.length
  const name = t(labels.title(resource))
  const fields = useMemo(() => editable(resource), [resource])
  const locked = record && opts.locked ? opts.locked(record) : false
  const may = (action: string) => (record ? ability.can(action, tag(resource.key, { ...record })) : ability.can(action, resource.key))
  // Documents (see docstatus.ts): drafts are edited freely, submitted ones only in their allow-on-submit fields
  const sub = resource.submittable
  const docstatus: DocStatus | undefined = sub ? (record ? docStatusOf(record) : 'draft') : undefined
  const lifecycle = (action: 'update' | 'delete' | 'submit' | 'cancel' | 'amend') => !docstatus || lifecycleAllows(docstatus, action)
  const afterSubmitFields = fieldsEditableAfterSubmit(resource)
  const editsAfterSubmit = docstatus === 'submitted' && afterSubmitFields.length > 0
  const canEditAfterSubmit = editsAfterSubmit && !locked && may('update')
  const canSave = !locked && (lifecycle('update') || (editsAfterSubmit && editingAfter)) && may(isNew ? 'create' : 'update')
  const canDelete = !locked && !isNew && lifecycle('delete') && may('delete')
  const canSubmit = !!sub && !locked && lifecycle('submit') && may('submit') && (!isNew || may('create'))
  const canCancel =
    !!sub && !isNew && lifecycle('cancel') && may('cancel') && (!record || !opts.cancellable || opts.cancellable(record, ability))
  const canAmend = !!sub && !isNew && lifecycle('amend') && may('amend')
  // A cancelled document is made again by amending, not copied: one way to correct it
  const canDuplicate = !!resource.duplicate && !isNew && docstatus !== 'cancelled' && ability.can('create', resource.key)
  const readOnlyField = (f: EditableField) => !canSave || (!!docstatus && !fieldEditable(sub, docstatus, f.name))
  const titleField = resource.field(resource.title)
  const titleOf = (r: Rec) => (titleField ? fieldText(titleField, r[titleField.name], { resource, refs, t, locale }) : '')
  const recordTitle = record ? titleOf(record) : ''
  const pageTitle = isNew ? t('form.newRecord', { name }) : recordTitle || (docstatus === 'draft' ? t('doc.draftTitle', { name }) : name)
  // On the page the status pill already says "draft"; the browser tab has no pill, so it keeps "(ร่าง)"
  const heading = isNew ? pageTitle : recordTitle || name
  const taskTarget = useMemo(
    () => ({ resource: resource.key, recordId: record ? String(record.id) : null, title: [name, recordTitle].filter(Boolean).join(' ') }),
    [resource.key, record, name, recordTitle],
  )
  useDocumentTitle(`${pageTitle} · ${t(`apps.${appId}`)}`)

  const initialValues = useMemo(() => {
    const defaults = isNew ? (opts.defaults?.({ me, params }) ?? {}) : {}
    // A copy's own date, number and files start as a new record's do
    const copied = source ? copyOf(resource, source) : {}
    return Object.fromEntries(fields.map((f) => [f.name, record?.[f.name] ?? copied[f.name] ?? defaults[f.name] ?? emptyValue(f)]))
  }, [resource, fields, record, source, isNew, opts, me, params])

  const schema = isNew ? resource.schema : resource.schema.partial()
  const validate = useMemo(() => schemaResolver(schema, { sync: true }), [schema])
  const form = useForm<Record<string, unknown>>({
    mode: 'controlled',
    initialValues,
    validate: (values) => validate(toBody(resource, values)),
    validateInputOnBlur: true,
  })

  // A newer version arrived (an action beside the form, a refetch): show it unless the user is editing
  // biome-ignore lint/correctness/useExhaustiveDependencies: `form` is a new object every render
  useEffect(() => {
    if (form.isDirty()) return
    form.setInitialValues(initialValues)
    form.setValues(initialValues)
    form.resetDirty(initialValues)
  }, [initialValues])

  const save = useSaveRecord(resource)
  const del = useDeleteRecord(resource)
  const life = useLifecycle(resource)
  // History and comments under every record (`activity: false` leaves them out)
  const showActivity = !isNew && opts.activity !== false
  const activity = useActivity(resource, showActivity ? record?.id : undefined)
  const editedAfterSubmit = new Set(activity.data?.afterSubmit.fields ?? [])
  // Back to the list as it was (filters, page) when we came from it; otherwise its first page
  const back = useGoBack()
  const goBack = () => back(`/${path}`)

  // On a phone a form kept under another page, or in another menu item's stack, stays as it was: only leaving it asks
  const { leaves } = useStackEntry()
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      leaves(currentLocation, nextLocation) && form.isDirty() && !busy && currentLocation.pathname !== nextLocation.pathname,
  )
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    modals.openConfirmModal({
      title: t('confirm.leaveTitle'),
      children: <Text size="sm">{t('confirm.leaveBody')}</Text>,
      labels: { confirm: t('confirm.leave'), cancel: t('confirm.stay') },
      confirmProps: { color: 'danger' },
      onConfirm: () => blocker.proceed(),
      onCancel: () => blocker.reset(),
    })
  }, [blocker, t])

  useHotkeys([['mod+S', () => formRef.current?.requestSubmit()]], [])

  // Long forms: open the tab (or step) holding the first problem and bring it into view
  const focusFirstError = (errors: FormErrors = form.errors) => {
    const first = fields.find((f) => errors[f.name])
    const holds = (sections: (string | string[])[]) => !!first && sections.flat().includes(first.form.section || 'main')
    const holder = tabs.find((tb) => holds(tb.sections))
    if (holder) setTab(holder.key)
    const step = stepped?.steps.findIndex((s) => holds(s.sections)) ?? -1
    if (step >= 0) setStepAt(step)
    requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
  }

  /** On to the next step once this one's fields are right, else to its first problem */
  const nextStep = () => {
    if (!stepped) return
    const here = stepped.steps[stepAt]?.sections.flat() ?? []
    const names = new Set(fields.filter((f) => here.includes(f.form.section || 'main')).map((f) => f.name))
    // Each problem where it is (a parcel line's weight, not the whole list), and only this step's
    const wrong = Object.entries(validate(toBody(resource, form.values))).filter(([path]) => names.has(path.split('.')[0] ?? path))
    if (wrong.length) {
      form.setErrors({ ...form.errors, ...Object.fromEntries(wrong) })
      return requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
    }
    setStepAt(fromReview ? stepped.steps.length : stepAt + 1)
    setFromReview(false)
    scrollToTop(formRef.current)
  }
  const goToStep = (key: string) => {
    const at = stepped?.steps.findIndex((s) => s.key === key) ?? -1
    if (at < 0) return
    setStepAt(at)
    setFromReview(true)
    scrollToTop(formRef.current)
  }

  /**
   * Enter in a one-line input: on to the next input (or the next step) instead of saving, when the page asks for it or
   * a step is being filled; an open list keeps Enter to choose
   */
  const onEnter = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
    if (!opts.enterMovesOn && !(stepped && !reviewing)) return
    const target = event.target
    if (!(target instanceof HTMLInputElement) || ['checkbox', 'radio', 'submit', 'button'].includes(target.type)) return
    if (target.getAttribute('aria-expanded') === 'true') return
    event.preventDefault()
    const inputs = [
      ...(formRef.current?.querySelectorAll<HTMLElement>(
        'input:not([type=hidden]):not([disabled]):not([readonly]), textarea:not([disabled])',
      ) ?? []),
    ].filter((el) => el.offsetParent !== null)
    const next = inputs[inputs.indexOf(target) + 1]
    if (next) next.focus()
    else if (stepped && !reviewing) nextStep()
  }

  const showError = (error: unknown) => {
    if (isApiError(error, 'VALIDATION')) {
      const errors = issuesToErrors(error.issues)
      form.setErrors(errors)
      focusFirstError(errors)
      notifications.show({ color: 'danger', message: t('errors.validation') })
    } else {
      notifications.show({
        color: 'danger',
        // A refused state change says why in the API's own words ("ยกเลิกใบเสร็จ … ก่อน")
        message: isApiError(error, 'CONFLICT')
          ? error.message || t('errors.conflict')
          : t(isApiError(error, 'FORBIDDEN') ? 'errors.forbidden' : 'errors.generic'),
      })
    }
  }

  /** Saves the form; the stored record, or undefined when the API refused */
  const saveValues = async (values: Record<string, unknown>, quiet = false): Promise<Rec | undefined> => {
    const body = toBody(resource, values)
    try {
      const { item } = await save.mutateAsync({
        id: record?.id,
        values: isNew ? body : changed(body, toBody(resource, form.getInitialValues())),
      })
      if (!quiet) notifications.show({ message: t(isNew ? 'status.created' : 'status.updated', { name }), color: 'primary' })
      form.resetDirty(values)
      return item
    } catch (error) {
      showError(error)
    }
  }

  const submit = form.onSubmit(
    async (typed) => {
      const andSubmit = thenSubmit.current
      thenSubmit.current = false
      // The page may ask first (is this someone already known?) and change what is saved
      const values = opts.beforeSave ? await opts.beforeSave(typed, { record, submitting: andSubmit, t }) : typed
      if (!values) {
        saveAndNew.current = false
        return
      }
      if (values !== typed) form.setValues(values)
      if (andSubmit) {
        const ok = await confirmAsync({
          title: t('doc.confirm.submitTitle', { name }),
          body: t('doc.confirm.submitBody'),
          confirm: t('doc.actions.submit'),
          cancel: t('actions.back'),
        })
        if (!ok) return
      }
      setBusy(andSubmit ? 'submit' : 'save')
      try {
        const saved = isNew || values !== typed || form.isDirty() ? await saveValues(values, andSubmit) : record
        if (!saved) return
        setEditingAfter(false)
        let submitted = false
        if (andSubmit) {
          try {
            const { item } = await life.submit.mutateAsync(saved.id)
            notifications.show({ message: t('doc.done.submit', { name, label: titleOf(item) }), color: 'primary' })
            submitted = true
          } catch (error) {
            showError(error)
          }
        }
        if (saveAndNew.current) {
          saveAndNew.current = false
          form.setValues(Object.fromEntries(fields.map((f) => [f.name, defaultsOf()[f.name] ?? emptyValue(f)])))
          form.resetDirty()
          navigate(`/${path}/new`, { replace: true })
        } else if (isNew || submitted) {
          // Submitted here and now: the page may follow on from it (`useJustSubmitted`)
          navigate(`/${path}/${saved.id}`, { replace: true, state: submitted ? justSubmitted : null })
        }
      } finally {
        setBusy(undefined)
      }
    },
    (errors) => {
      thenSubmit.current = false
      focusFirstError(errors)
    },
  )

  const amend = async () => {
    if (!record) return
    setBusy('amend')
    try {
      const { item } = await life.amend.mutateAsync(record.id)
      notifications.show({ message: t('doc.done.amend', { label: recordTitle }), color: 'primary' })
      navigate(`/${path}/${item.id}`)
    } catch (error) {
      showError(error)
    } finally {
      setBusy(undefined)
    }
  }

  const duplicate = () => record && navigate(`/${path}/new?copy=${encodeURIComponent(record.id)}`)

  const askDelete = () =>
    modals.openConfirmModal({
      title: t('confirm.deleteTitle', { name }),
      children: <Text size="sm">{t('confirm.deleteBody', { label: recordTitle })}</Text>,
      labels: { confirm: t('actions.delete'), cancel: t('actions.cancel') },
      confirmProps: { color: 'danger' },
      onConfirm: async () => {
        await del.mutateAsync(record?.id as string)
        form.resetDirty()
        notifications.show({ message: t('status.deleted', { name }) })
        navigate(`/${path}`)
      },
    })

  const bySection = new Map<string, EditableField[]>()
  const omitted = opts.omit?.(form.values as Record<string, unknown>) ?? []
  for (const f of fields) {
    if (f.form.hidden || omitted.includes(f.name)) continue
    const section = f.form.section || 'main'
    bySection.set(section, [...(bySection.get(section) ?? []), f])
  }

  // `untitled`: a step of its own already says what it is
  const renderSection = (section: string, untitled = false) =>
    bySection.has(section) && (
      <SectionCard key={section} title={untitled ? undefined : t(labels.section(resource, section), { defaultValue: '' }) || undefined}>
        <Grid gap="md">
          {(bySection.get(section) ?? []).map((f) => (
            <Grid.Col
              key={f.name}
              span={{ base: 12, sm: f.form.span ?? 6 }}
              data-readonly={readOnlyField(f) || undefined}
              data-edited={editedAfterSubmit.has(f.name) || undefined}
            >
              {renderField(f)}
            </Grid.Col>
          ))}
        </Grid>
      </SectionCard>
    )
  // One row per entry; an array is sections side by side (sender | receiver)
  const sectionRows = (rows: (string | string[])[], untitled = false) =>
    rows
      .filter((row) => [row].flat().some((s) => bySection.has(s)))
      .map((row) => (
        <div key={[row].flat().join('|')} className={classes.row} data-pair={Array.isArray(row) || undefined}>
          {[row].flat().map((section) => renderSection(section, untitled))}
        </div>
      ))
  const tabHasError = (tb: { sections: (string | string[])[] }) =>
    fields.some((f) => form.errors[f.name] && tb.sections.flat().includes(f.form.section || 'main'))

  const renderField = (f: EditableField): ReactNode => {
    const custom = f.form.custom ? opts.customFields?.[f.name] : undefined
    const input = custom ? (
      custom({ form, field: f, record, refs, readOnly: readOnlyField(f), t })
    ) : (
      <FieldInput resource={resource} field={f} form={form} t={t} locale={locale} readOnly={readOnlyField(f)} />
    )
    if (!f.warnTaken || readOnlyField(f)) return input
    return (
      <>
        {input}
        <TakenWarning resource={resource} field={f} form={form} record={record} path={path} />
      </>
    )
  }

  const stopEditing = () => {
    form.reset()
    setEditingAfter(false)
  }
  // The saves: beside the title on a computer, in the bar at the bottom of a phone. They sit outside the
  // <form> (`form={formId}`); the first one in the page is what Enter in an input presses, so plain save comes first
  const saveButtons =
    docstatus === 'draft' ? (
      <>
        <Button
          form={formId}
          variant={canSubmit ? 'default' : 'filled'}
          type="submit"
          loading={busy === 'save'}
          disabled={(!isNew && !form.isDirty()) || busy === 'submit'}
        >
          {t(isNew ? 'doc.actions.saveDraft' : 'actions.save')}
        </Button>
        {canSubmit && (
          <Button
            form={formId}
            type="submit"
            onClick={() => (thenSubmit.current = true)}
            loading={busy === 'submit'}
            disabled={busy === 'save'}
          >
            {t(isNew || form.isDirty() ? 'doc.actions.saveAndSubmit' : 'doc.actions.submit')}
          </Button>
        )}
      </>
    ) : (
      <>
        {isNew && !phone && !sub && (
          <Button
            form={formId}
            variant="light"
            type="submit"
            onClick={() => (saveAndNew.current = true)}
            loading={busy === 'save' && saveAndNew.current}
          >
            {t('actions.saveAndNew')}
          </Button>
        )}
        <Button form={formId} type="submit" loading={busy === 'save' && !saveAndNew.current} disabled={!isNew && !form.isDirty()}>
          {t('actions.save')}
        </Button>
      </>
    )

  const headerActions = (
    <>
      {record && opts.actions?.(record, refs)}
      {canEditAfterSubmit && !editingAfter && (
        <Button variant="default" leftSection={<IconPencil size={18} />} onClick={() => setEditingAfter(true)}>
          {t('doc.actions.editAfterSubmit')}
        </Button>
      )}
      {canAmend && (
        <Button leftSection={<IconCopy size={18} />} onClick={amend} loading={busy === 'amend'}>
          {t('doc.actions.amend')}
        </Button>
      )}
      {(canDelete || canDuplicate || canCancel) && (
        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon size={40} aria-label={t('actions.more')}>
              <IconDotsVertical size={22} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {canDuplicate && (
              <Menu.Item leftSection={<IconCopy size={18} />} onClick={duplicate}>
                {t('actions.duplicate')}
              </Menu.Item>
            )}
            {canCancel && (
              <Menu.Item color="danger" leftSection={<IconBan size={18} />} onClick={() => setCancelling(true)}>
                {t('doc.actions.cancel')}
              </Menu.Item>
            )}
            {canDelete && (
              <Menu.Item color="danger" leftSection={<IconTrash size={18} />} onClick={askDelete}>
                {t('actions.delete')}
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      )}
      <GuideButton resource={resource} />
      {/* No "cancel" beside them: the back arrow by the title leaves, and asks first when something is unsaved */}
      {canSave && !phone && (
        <div className={classes.saveActions}>
          {editingAfter && (
            <Button variant="default" onClick={stopEditing}>
              {t('doc.actions.stopEditing')}
            </Button>
          )}
          {saveButtons}
        </div>
      )}
    </>
  )

  // "every field", or the names of the few that can change
  const allowedAfterSubmit = !canEditAfterSubmit
    ? []
    : sub?.allowOnSubmit === 'all'
      ? [t('doc.banner.allFields')]
      : afterSubmitFields.map((n) => t(resource.field(n)?.label ?? n))

  return (
    <Page
      title={
        <Group gap={4} wrap="nowrap" component="span">
          <ActionIcon size={40} onClick={goBack} aria-label={t('actions.back')} ml={-10}>
            <IconArrowLeft size={22} stroke={1.75} />
          </ActionIcon>
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{heading}</span>
          {sub && <DocStatusPill resource={resource} record={record} />}
          {sub && <AfterSubmitBadge resource={resource} activity={activity.data} locale={locale} />}
        </Group>
      }
      actions={headerActions}
    >
      {/* A field asked of a phone goes on this record once it is saved */}
      <TaskTargetProvider value={taskTarget}>
        <form
          ref={formRef}
          id={formId}
          onSubmit={submit}
          onKeyDown={onEnter}
          className={classes.form}
          data-locked={!canSave || undefined}
          noValidate
        >
          <div
            className={opts.aside && !stepped ? classes.layoutAside : classes.layout}
            data-details-last={(record && opts.detailsLast?.(record)) || undefined}
          >
            {/* The form and its side panel: the panel stays in view beside the form, not over what comes under both */}
            <div className={classes.top}>
              <div className={classes.sections}>
                <div className={classes.notices}>
                  {locked && (
                    <Alert variant="light" color="gray" radius="lg" icon={<IconLock size={20} stroke={1.75} />}>
                      {t(locked)}
                    </Alert>
                  )}
                  {source && (
                    <Alert variant="light" color="gray" radius="lg" icon={<IconCopy size={20} stroke={1.75} />}>
                      {t('form.copiedFrom', { label: titleOf(source) || name })}
                    </Alert>
                  )}
                  {sub && record && (
                    <DocBanner
                      resource={resource}
                      path={path}
                      record={record}
                      refs={refs}
                      editableFields={allowedAfterSubmit}
                      editing={editingAfter}
                      locale={locale}
                    />
                  )}
                </div>
                {stepped ? (
                  <>
                    <div className={classes.stepHead}>
                      <div
                        className={classes.stepBars}
                        role="progressbar"
                        aria-valuemin={1}
                        aria-valuemax={stepped.steps.length + 1}
                        aria-valuenow={stepAt + 1}
                      >
                        {[...stepped.steps, { key: 'review' }].map((s, i) => (
                          <span key={s.key} data-on={i <= stepAt || undefined} />
                        ))}
                      </div>
                      <Text size="xs" c="dimmed">
                        {t('form.step', { step: stepAt + 1, steps: stepped.steps.length + 1 })}
                      </Text>
                      <Text fw={500} size="lg">
                        {t(reviewing ? stepped.reviewLabel : (stepped.steps[stepAt]?.label ?? ''))}
                      </Text>
                    </div>
                    {reviewing
                      ? stepped.review(form, refs, goToStep)
                      : sectionRows(stepped.steps[stepAt]?.sections ?? [], (stepped.steps[stepAt]?.sections.length ?? 0) === 1)}
                  </>
                ) : tabs.length > 1 ? (
                  <Tabs value={tab} onChange={(v) => v && setTab(v)} color="primary" keepMounted classNames={{ tab: classes.tab }}>
                    <Tabs.List className={classes.tabs}>
                      {tabs.map((tb) => (
                        <Tabs.Tab key={tb.key} value={tb.key} rightSection={tabHasError(tb) ? <span className={classes.tabError} /> : null}>
                          {t(tb.label)}
                        </Tabs.Tab>
                      ))}
                    </Tabs.List>
                    {tabs.map((tb) => (
                      <Tabs.Panel key={tb.key} value={tb.key}>
                        {sectionRows(tb.sections)}
                      </Tabs.Panel>
                    ))}
                  </Tabs>
                ) : (
                  sectionRows(tabs[0]?.sections ?? [])
                )}
              </div>
              {opts.aside && !stepped && <aside className={classes.aside}>{opts.aside(record, refs, form)}</aside>}
            </div>
            {/* Under the form and its side panel, as wide as both; the history under it, beside nothing */}
            {record && !stepped && opts.below && <div className={classes.below}>{opts.below(record, refs)}</div>}
            {showActivity && record && (
              <div className={classes.history}>
                <DocActivity resource={resource} recordId={record.id} activity={activity.data} locale={locale} />
              </div>
            )}
          </div>
          {canSave && stepped && !reviewing && (
            <div className={classes.footer} data-steps>
              {stepped.summary && <div className={classes.stepSummary}>{stepped.summary(form)}</div>}
              <Button variant="default" onClick={() => (stepAt > 0 ? setStepAt(stepAt - 1) : goBack())}>
                {stepAt > 0 ? t('actions.back') : t('actions.cancel')}
              </Button>
              <Button onClick={nextStep}>{t(fromReview ? 'form.toReview' : 'actions.next')}</Button>
            </div>
          )}
          {canSave && phone && (!stepped || reviewing) && (
            <div className={classes.footer}>
              {reviewing && (
                // Only an arrow, so both saves keep their whole label on a phone
                <Tooltip label={t('actions.back')}>
                  <ActionIcon
                    variant="default"
                    size="input-sm"
                    className={classes.stepBack}
                    aria-label={t('actions.back')}
                    onClick={() => setStepAt(stepAt - 1)}
                  >
                    <IconArrowLeft size={20} stroke={1.75} />
                  </ActionIcon>
                </Tooltip>
              )}
              {!canSubmit && !reviewing && (
                <Button variant="default" onClick={editingAfter ? stopEditing : goBack}>
                  {t(editingAfter ? 'doc.actions.stopEditing' : 'actions.cancel')}
                </Button>
              )}
              {saveButtons}
            </div>
          )}
          {cancelling && record && (
            <CancelDialog
              title={t('doc.confirm.cancelTitle', { name, label: recordTitle })}
              required={sub?.cancelReason === 'required'}
              onClose={() => setCancelling(false)}
              onCancel={async (reason) => {
                await life.cancel.mutateAsync({ id: record.id, reason })
                notifications.show({ message: t('doc.done.cancel', { name, label: recordTitle }) })
              }}
              t={t}
            />
          )}
        </form>
      </TaskTargetProvider>
    </Page>
  )
}
